import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  allocationRulesApi,
  ruleDraftChanges,
  ruleInvalidFields,
  type AllocationRule,
  type RuleDraft,
  type RuleTargetMode,
} from '@/api/allocationRules';
import { accountsApi, type Account } from '@/api/accounts';
import { categoriesApi, type Category } from '@/api/categories';
import { ErrorCodes, isApiError } from '@/api/errors';
import { translateServerPhrase } from '@/features/shared/useFieldErrors';
import { bpToInputText, minorToInputText, parseMoneyToMinor, percentToBp } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useOwnedEnvelopes } from '@/features/accounts/useOwnedEnvelopes';
import { currencyRefusedRows } from '@/features/accounts/allocationErrors';
import { CategoryChip } from '@/components/CategoryChip';
import { ErrorSummary, type ErrorSummaryItem } from '@/components/ErrorSummary';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { useReturnFocus } from '@/components/useConfirm';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { resolveTokens } from '@/theme/theme';
import { unmanagedOutlineSx } from '@/features/accounts/unmanagedStyle';
import { DRY_RUN_TITLE_ID, DryRunBody, type DryRunSource } from './DryRunPanel';

const TITLE_ID = 'rule-editor-title';
const SUBSCRIBE_TITLE_ID = 'rule-editor-subscribe-title';
const SAVE_OPENER = 'rule-editor-save';
const DRY_RUN_OPENER = 'rule-dry-run';
const NONE = '';
const NAME_MAX = 120;
const CONTAINS_MAX = 255;

interface TargetRow {
  key: string;
  envelopeId: number | '';
  mode: RuleTargetMode;
  amount: string;
  percent: string;
  categoryId: number | '';
}

interface RuleEditorDialogProps {
  accountId: number;
  currency: string | null;
  timeZone: string;
  rule: AllocationRule | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}

/** A target's envelope field: the first keeps the id the summary's targets entry focuses. */
function ruleTargetId(key: string, index: number): string {
  return index === 0 ? 'rule-target-first' : `rule-target-${key}`;
}

/** Editable amount / percent text is in the locale's separators — the parsers read it back. */
function rowFromTarget(
  target: AllocationRule['targets'][number],
  currency: string | null,
  locale: string,
): TargetRow {
  return {
    key: String(target.id ?? `t-${target.sortOrder}`),
    envelopeId: target.envelopeId,
    mode: target.mode,
    amount:
      target.fixedAmountMinor !== null
        ? minorToInputText(Math.abs(target.fixedAmountMinor), currency, locale)
        : '',
    percent: target.percentBp !== null ? bpToInputText(target.percentBp, locale) : '',
    categoryId: target.categoryId ?? '',
  };
}

function emptyTarget(key: string): TargetRow {
  return {
    key,
    envelopeId: '',
    mode: 'FIXED',
    amount: '',
    percent: '',
    categoryId: '',
  };
}

/**
 * Create / edit rule editor (TMMAPP-0017): name, match criteria, ordered FIXED then PERCENT
 * targets. Save sends the full draft; dry run opens the panel with `{ rule: draft }`.
 */
export function RuleEditorDialog({
  accountId,
  currency,
  timeZone,
  rule,
  onClose,
  onSaved,
}: RuleEditorDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const fullScreen = useDialogFullScreen();
  const { envelopes } = useOwnedEnvelopes();
  const [name, setName] = useState(rule?.name ?? '');
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [contains, setContains] = useState(rule?.matchPayeeContains ?? '');
  const [regex, setRegex] = useState(rule?.matchPayeeRegex ?? '');
  const [amountFrom, setAmountFrom] = useState(
    rule?.matchAmountMinMinor != null
      ? minorToInputText(Math.abs(rule.matchAmountMinMinor), currency, locale)
      : '',
  );
  const [amountTo, setAmountTo] = useState(
    rule?.matchAmountMaxMinor != null
      ? minorToInputText(Math.abs(rule.matchAmountMaxMinor), currency, locale)
      : '',
  );
  const [targets, setTargets] = useState<TargetRow[]>(
    rule !== null && rule.targets.length > 0
      ? rule.targets.map((target) => rowFromTarget(target, currency, locale))
      : [emptyTarget('n1')],
  );
  const [ruleMode, setRuleMode] = useState<'ALLOCATE' | 'TRANSFER'>(
    rule?.transferAccountId != null ? 'TRANSFER' : 'ALLOCATE',
  );
  const [transferAccountId, setTransferAccountId] = useState<number | ''>(
    rule?.transferAccountId ?? '',
  );
  const [otherAccounts, setOtherAccounts] = useState<Account[]>([]);
  const [nextKey, setNextKey] = useState(2);
  const [nameError, setNameError] = useState<string | null>(null);
  const [regexError, setRegexError] = useState<string | null>(null);
  const [matchError, setMatchError] = useState<string | null>(null);
  const [targetsError, setTargetsError] = useState<string | null>(null);
  // A target refused on save, by row key (TMMAPI-0205's currency refusal).
  const [targetRowErrors, setTargetRowErrors] = useState<Record<string, string>>({});
  // The dry run closed on a currency refusal: focus goes to the marked target, not to Dry run.
  const [dryRunRefused, setDryRunRefused] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [dryRun, setDryRun] = useState<DryRunSource | null>(null);
  // SUBSCRIPTION_REQUIRED shows the subscribe card in place of the form (TMMAPP-0097 §C 25).
  const [subscribeRequired, setSubscribeRequired] = useState(false);
  const [categoriesByVirtualAccount, setCategoriesByVirtualAccount] = useState<
    Record<number, Category[]>
  >({});
  const { dirty } = useDirty({
    name,
    enabled,
    contains,
    regex,
    amountFrom,
    amountTo,
    targets,
    ruleMode,
    transferAccountId,
  });
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);

  const groups = useMemo(() => {
    const g: { name: string; envelopes: typeof envelopes }[] = [];
    for (const envelope of envelopes) {
      const existing = g.find((x) => x.name === envelope.virtualAccountName);
      if (existing) existing.envelopes.push(envelope);
      else g.push({ name: envelope.virtualAccountName, envelopes: [envelope] });
    }
    return g;
  }, [envelopes]);

  const managedPeers = useMemo(
    () => otherAccounts.filter((a) => a.management === 'MANAGED'),
    [otherAccounts],
  );
  const unmanagedPeers = useMemo(
    () => otherAccounts.filter((a) => a.management === 'UNMANAGED'),
    [otherAccounts],
  );

  useEffect(() => {
    let cancelled = false;
    void accountsApi
      .list()
      .then((list) => {
        if (!cancelled) {
          setOtherAccounts(list.filter((a) => a.id !== accountId && a.access === 'OWNER'));
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  // Categories belong to the virtual account (TMMAPI-0145): one list per targeted envelope's owner.
  const virtualAccountIds = targets
    .map((row) => envelopes.find((v) => v.id === row.envelopeId)?.virtualAccountId)
    .filter((id): id is number => typeof id === 'number');

  useEffect(() => {
    const missing = virtualAccountIds.filter((id) => categoriesByVirtualAccount[id] === undefined);
    if (missing.length === 0) return;
    let cancelled = false;
    void Promise.all(
      [...new Set(missing)].map(async (id) => {
        try {
          return [id, await categoriesApi.list(id)] as const;
        } catch {
          return [id, [] as Category[]] as const;
        }
      }),
    ).then((rows) => {
      if (cancelled) return;
      setCategoriesByVirtualAccount((prev) => {
        const next = { ...prev };
        for (const [id, list] of rows) next[id] = list;
        return next;
      });
    });
    return () => {
      cancelled = true;
    };
  }, [virtualAccountIds, categoriesByVirtualAccount]);

  const minMinor = parseBound(amountFrom, currency, locale);
  const maxMinor = parseBound(amountTo, currency, locale);
  const bound =
    minMinor !== null && maxMinor !== null ? Math.min(minMinor, maxMinor) : (minMinor ?? maxMinor);

  const percentBpSum = targets.reduce((sum, row) => {
    if (row.mode !== 'PERCENT') return sum;
    const bp = percentToBp(row.percent, locale);
    return bp !== null ? sum + bp : sum;
  }, 0);
  const percentOver = percentBpSum > 10_000;

  const fixedSum = targets.reduce((sum, row) => {
    if (row.mode !== 'FIXED') return sum;
    const n = parseMoneyToMinor(row.amount, currency, locale);
    return n !== null ? sum + Math.abs(n) : sum;
  }, 0);
  const fixedOver = bound !== null && fixedSum > bound;

  const targetNonPositive = targets.some((row) => {
    if (typeof row.envelopeId !== 'number') return false;
    if (row.mode === 'FIXED') {
      const n = parseMoneyToMinor(row.amount, currency, locale);
      return n === null || n === 0;
    }
    const bp = percentToBp(row.percent, locale);
    return bp === null || bp === 0;
  });

  function collectDraft(): RuleDraft | null {
    const trimmed = name.trim();
    if (trimmed.length === 0 || trimmed.length > NAME_MAX) return null;
    if (ruleMode === 'TRANSFER') {
      if (typeof transferAccountId !== 'number') return null;
      const draft: RuleDraft = {
        name: trimmed,
        enabled,
        targets: [],
        transferAccountId,
      };
      const c = contains.trim();
      if (c.length > 0) draft.matchPayeeContains = c;
      const r = regex.trim();
      if (r.length > 0) draft.matchPayeeRegex = r;
      if (minMinor !== null) draft.matchAmountMinMinor = minMinor;
      if (maxMinor !== null) draft.matchAmountMaxMinor = maxMinor;
      return draft;
    }
    const collected: RuleDraft['targets'] = [];
    for (const [index, row] of targets.entries()) {
      if (typeof row.envelopeId !== 'number') continue;
      if (row.mode === 'FIXED') {
        const n = parseMoneyToMinor(row.amount, currency, locale);
        if (n === null || n === 0) continue;
        collected.push({
          envelopeId: row.envelopeId,
          categoryId: typeof row.categoryId === 'number' ? row.categoryId : null,
          mode: 'FIXED',
          fixedAmountMinor: Math.abs(n),
          percentBp: null,
          sortOrder: index,
        });
      } else {
        const bp = percentToBp(row.percent, locale);
        if (bp === null || bp === 0) continue;
        collected.push({
          envelopeId: row.envelopeId,
          categoryId: typeof row.categoryId === 'number' ? row.categoryId : null,
          mode: 'PERCENT',
          fixedAmountMinor: null,
          percentBp: Math.abs(bp),
          sortOrder: index,
        });
      }
    }
    if (collected.length === 0) return null;
    const draft: RuleDraft = {
      name: trimmed,
      enabled,
      targets: collected,
      transferAccountId: null,
    };
    const c = contains.trim();
    if (c.length > 0) draft.matchPayeeContains = c;
    const r = regex.trim();
    if (r.length > 0) draft.matchPayeeRegex = r;
    if (minMinor !== null) draft.matchAmountMinMinor = minMinor;
    if (maxMinor !== null) draft.matchAmountMaxMinor = maxMinor;
    return draft;
  }

  function hasMatch(): boolean {
    return (
      contains.trim().length > 0 ||
      regex.trim().length > 0 ||
      amountFrom.trim().length > 0 ||
      amountTo.trim().length > 0
    );
  }

  async function handleSave() {
    setNameError(null);
    setRegexError(null);
    setMatchError(null);
    setTargetsError(null);
    setTargetRowErrors({});
    setFormError(null);
    if (name.trim().length === 0 || name.trim().length > NAME_MAX) {
      setNameError(t('rules.name'));
      guard.focusErrors();
      return;
    }
    if (!hasMatch()) {
      setMatchError(t('rules.matchRequired'));
      guard.focusErrors();
      return;
    }
    if (ruleMode === 'ALLOCATE') {
      if (percentOver) {
        setTargetsError(t('rules.percentSum'));
        guard.focusErrors();
        return;
      }
      if (fixedOver) {
        setTargetsError(t('rules.fixedSum'));
        guard.focusErrors();
        return;
      }
      if (targetNonPositive) {
        setTargetsError(t('rules.targetPositive'));
        guard.focusErrors();
        return;
      }
    } else if (typeof transferAccountId !== 'number') {
      setTargetsError(t('transfer.account'));
      guard.focusErrors();
      return;
    }
    const draft = collectDraft();
    if (draft === null) {
      setTargetsError(t(ruleMode === 'TRANSFER' ? 'transfer.account' : 'rules.targetPositive'));
      guard.focusErrors();
      return;
    }
    setSubmitting(true);
    try {
      if (rule === null) await allocationRulesApi.create(accountId, draft);
      else await allocationRulesApi.patch(accountId, rule.id, ruleDraftChanges(rule, draft));
      onSaved(t('rules.saved'));
      onClose();
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setSubscribeRequired(true);
      } else if (isApiError(err) && err.code === ErrorCodes.RULE_EXISTS) {
        setNameError(t('rules.nameTaken'));
        guard.focusErrors();
      } else if (isApiError(err) && err.code === ErrorCodes.RULE_INVALID) {
        applyInvalid(ruleInvalidFields(err));
        guard.focusErrors();
      } else if (isApiError(err) && err.code === ErrorCodes.ALLOCATION_CURRENCY_MISMATCH) {
        // With no target bound there (or no `details`), the sentence sits on the targets.
        if (!markCurrencyRefusal(err)) setTargetsError(t('allocations.errorCurrencyMismatch'));
        guard.focusErrors();
      } else {
        setFormError(t('accounts.errorGeneric'));
        guard.focusErrors();
      }
    } finally {
      setSubmitting(false);
    }
  }

  function applyInvalid(fields: Record<string, string>) {
    fields = Object.fromEntries(
      Object.entries(fields).map(([key, value]) => [key, translateServerPhrase(value, t)]),
    );
    if (fields.name !== undefined) setNameError(fields.name);
    if (fields.matchPayeeRegex !== undefined) setRegexError(fields.matchPayeeRegex);
    const match = fields.match ?? fields.matchPayeeContains;
    if (match !== undefined && fields.matchPayeeRegex === undefined) setMatchError(match);
    if (fields.match !== undefined) setMatchError(fields.match);
    const targetBits = Object.entries(fields)
      .filter(([key]) => key === 'targets' || key.startsWith('targets['))
      .map(([, value]) => value);
    if (targetBits.length > 0) setTargetsError(targetBits.join(' '));
    const leftovers = Object.entries(fields)
      .filter(
        ([key]) =>
          key !== 'name' &&
          key !== 'match' &&
          key !== 'matchPayeeContains' &&
          key !== 'matchPayeeRegex' &&
          key !== 'targets' &&
          !key.startsWith('targets['),
      )
      .map(([, value]) => value);
    if (leftovers.length > 0) setFormError(t('errors.detailsAttention'));
  }

  /**
   * A target into a virtual account in another currency (TMMAPI-0205): marks the targets bound for
   * the refused virtual account; false when none is (or the refusal has no `details`).
   */
  function markCurrencyRefusal(err: unknown): boolean {
    const refused = currencyRefusedRows(
      err,
      targets.map((row) => ({
        key: row.key,
        virtualAccountId: envelopes.find((v) => v.id === row.envelopeId)?.virtualAccountId,
      })),
    );
    if (refused.length === 0) return false;
    const message = t('allocations.errorCurrencyMismatch');
    setTargetRowErrors(Object.fromEntries(refused.map((key) => [key, message])));
    return true;
  }

  /** The dry run's currency refusal: back to the form with the target marked, when one matches. */
  function handleDryRunRefused(err: unknown): boolean {
    if (!markCurrencyRefusal(err)) return false;
    setDryRunRefused(true);
    setDryRun(null);
    guard.focusErrors();
    return true;
  }

  function handleDryRun() {
    if (!hasMatch()) {
      setMatchError(t('rules.matchRequired'));
      return;
    }
    const draft = collectDraft();
    if (draft === null) {
      setFormError(t('rules.matchRequired'));
      return;
    }
    setTargetRowErrors({});
    setDryRunRefused(false);
    setDryRun({ rule: draft });
  }

  const title = rule === null ? t('rules.new') : t('rules.edit');
  // Closing the dry run returns focus to the button that opened it (DLG-004).
  // Back from a refused dry run, focus goes to the marked target instead (`focusErrors`).
  useReturnFocus(dryRun !== null, dryRunRefused ? null : DRY_RUN_OPENER);
  // Back from the subscribe card, focus returns to the Save that asked (TMMAPP-0097 §C 25).
  useReturnFocus(subscribeRequired, SAVE_OPENER);

  // The fields in error, in the form's visual order, for the summary a failed save focuses.
  const summaryItems: ErrorSummaryItem[] = [
    { id: 'rule-name', label: t('rules.name'), message: nameError },
    { id: 'rule-contains', label: t('rules.matchTitle'), message: matchError },
    { id: 'rule-regex', label: t('rules.payeeRegex'), message: regexError },
    {
      id: ruleMode === 'TRANSFER' ? 'rule-transfer-target' : 'rule-target-first',
      label: t('rules.targetsTitle'),
      message: targetsError,
    },
    ...targets.map((row, index) => ({
      id: ruleTargetId(row.key, index),
      label: t('rules.targetEnvelope'),
      message: targetRowErrors[row.key] ?? null,
    })),
  ].flatMap(({ message, ...item }) => (message === null ? [] : [{ ...item, message }]));

  // The dry run shows in place of the form, in this same dialog — dialogs never stack (DLG-005).
  // Both returns are the same `GuardedDialog`, so the one `Dialog` stays mounted across the swap;
  // every field lives in this component's state, so the form is back as it was when the dry run closes.
  if (subscribeRequired) {
    return (
      <GuardedDialog
        guard={guard}
        confirm={{ titleId: SUBSCRIBE_TITLE_ID, onClose: () => setSubscribeRequired(false) }}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
      >
        <DialogContent>
          <SubscribeInPlace titleId={SUBSCRIBE_TITLE_ID} focusTitle onClose={onClose} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubscribeRequired(false)}>{t('common.cancel')}</Button>
        </DialogActions>
      </GuardedDialog>
    );
  }

  if (dryRun !== null) {
    const closeDryRun = () => setDryRun(null);
    return (
      <GuardedDialog
        guard={guard}
        confirm={{ titleId: DRY_RUN_TITLE_ID, onClose: closeDryRun }}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
      >
        <DryRunBody
          accountId={accountId}
          currency={currency}
          timeZone={timeZone}
          source={dryRun}
          onClose={closeDryRun}
          onRefused={handleDryRunRefused}
        />
      </GuardedDialog>
    );
  }

  return (
    <>
      <GuardedDialog
        guard={guard}
        titleId={TITLE_ID}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle id={TITLE_ID}>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <ErrorSummary items={summaryItems} />
            {formError !== null && <Alert severity="error">{formError}</Alert>}
            <TextField
              id="rule-name"
              label={t('rules.name')}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              fullWidth
              slotProps={{ htmlInput: { maxLength: NAME_MAX } }}
              error={nameError !== null}
              helperText={nameError}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                  slotProps={{ input: { 'aria-label': t('rules.enabled') } }}
                />
              }
              label={t('rules.enabled')}
            />
            <Typography variant="subtitle2" component="h3">
              {t('rules.matchTitle')}
            </Typography>
            {matchError !== null && <Alert severity="error">{matchError}</Alert>}
            <TextField
              id="rule-contains"
              label={t('rules.payeeContains')}
              value={contains}
              onChange={(event) => setContains(event.target.value)}
              fullWidth
              slotProps={{ htmlInput: { maxLength: CONTAINS_MAX } }}
            />
            <TextField
              id="rule-regex"
              label={t('rules.payeeRegex')}
              value={regex}
              onChange={(event) => setRegex(event.target.value)}
              fullWidth
              error={regexError !== null}
              helperText={regexError ?? t('rules.regexHelp')}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 2 }}>
              <TextField
                label={t('rules.amountFrom')}
                value={amountFrom}
                onChange={(event) => setAmountFrom(event.target.value)}
                fullWidth
              />
              <TextField
                label={t('rules.amountTo')}
                value={amountTo}
                onChange={(event) => setAmountTo(event.target.value)}
                fullWidth
              />
            </Stack>
            <FormHelperText>{t('rules.amountHelp')}</FormHelperText>
            <Typography variant="subtitle2" component="h3">
              {t('rules.targetsTitle')}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={ruleMode}
              onChange={(_e, value: 'ALLOCATE' | 'TRANSFER' | null) => {
                if (value !== null) setRuleMode(value);
              }}
              aria-label={t('rules.targetsTitle')}
            >
              <ToggleButton value="ALLOCATE">{t('rules.allocateMode')}</ToggleButton>
              <ToggleButton value="TRANSFER">{t('rules.transferMode')}</ToggleButton>
            </ToggleButtonGroup>
            {ruleMode === 'TRANSFER' ? (
              <FormControl fullWidth size="small">
                <InputLabel id="rule-transfer-account">{t('transfer.account')}</InputLabel>
                <Select
                  labelId="rule-transfer-account"
                  SelectDisplayProps={{ id: 'rule-transfer-target' }}
                  label={t('transfer.account')}
                  value={transferAccountId === '' ? '' : String(transferAccountId)}
                  onChange={(event) => {
                    const raw = event.target.value;
                    setTransferAccountId(raw === '' ? '' : Number(raw));
                  }}
                >
                  <ListSubheader>{t('transfer.groupManaged')}</ListSubheader>
                  {managedPeers.map((a) => (
                    <MenuItem key={a.id} value={String(a.id)}>
                      {a.name}
                    </MenuItem>
                  ))}
                  {unmanagedPeers.length > 0 && (
                    <ListSubheader>{t('transfer.groupUnmanaged')}</ListSubheader>
                  )}
                  {unmanagedPeers.map((a) => (
                    <MenuItem key={a.id} value={String(a.id)} sx={unmanagedOutlineSx(tokens)}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
                {targetsError !== null && <FormHelperText error>{targetsError}</FormHelperText>}
              </FormControl>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('rules.targetsOrder')}
                </Typography>
                {percentOver && <Alert severity="error">{t('rules.percentSum')}</Alert>}
                {fixedOver && <Alert severity="error">{t('rules.fixedSum')}</Alert>}
                {targetsError !== null && <Alert severity="error">{targetsError}</Alert>}
                {targets.map((row, index) => {
                  const envelopeId = typeof row.envelopeId === 'number' ? row.envelopeId : null;
                  const envelope =
                    envelopeId !== null ? envelopes.find((v) => v.id === envelopeId) : undefined;
                  const cats = (
                    envelopeId !== null
                      ? (categoriesByVirtualAccount[envelope?.virtualAccountId ?? -1] ??
                        envelope?.categories ??
                        [])
                      : []
                  ).filter((c) => c.archivedAt === null);
                  return (
                    <Stack key={row.key} direction={{ xs: 'column', md: 'row' }} sx={{ gap: 1 }}>
                      <FormControl
                        size="small"
                        sx={{ minWidth: 200, flex: 1 }}
                        error={targetRowErrors[row.key] !== undefined}
                      >
                        <InputLabel id={`rule-envelope-${row.key}`}>
                          {t('rules.targetEnvelope')}
                        </InputLabel>
                        <Select
                          labelId={`rule-envelope-${row.key}`}
                          SelectDisplayProps={{ id: ruleTargetId(row.key, index) }}
                          label={t('rules.targetEnvelope')}
                          value={row.envelopeId === '' ? NONE : String(row.envelopeId)}
                          onChange={(event) => {
                            const v = event.target.value;
                            setTargets((prev) =>
                              prev.map((r) =>
                                r.key === row.key
                                  ? {
                                      ...r,
                                      envelopeId: v === NONE ? '' : Number(v),
                                      categoryId: '',
                                    }
                                  : r,
                              ),
                            );
                          }}
                        >
                          <MenuItem value={NONE}>{t('rules.targetEnvelope')}</MenuItem>
                          {groups.flatMap((g) => [
                            <ListSubheader key={`h-${g.name}`}>{g.name}</ListSubheader>,
                            ...g.envelopes.map((v) => (
                              <MenuItem key={v.id} value={String(v.id)}>
                                {v.name}
                              </MenuItem>
                            )),
                          ])}
                        </Select>
                        {targetRowErrors[row.key] !== undefined && (
                          <FormHelperText>{targetRowErrors[row.key]}</FormHelperText>
                        )}
                      </FormControl>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={row.mode}
                        onChange={(_event, value: RuleTargetMode | null) => {
                          if (value !== null) {
                            setTargets((prev) =>
                              prev.map((r) => (r.key === row.key ? { ...r, mode: value } : r)),
                            );
                          }
                        }}
                        aria-label={t('rules.targetMode')}
                      >
                        <ToggleButton value="FIXED">{t('rules.fixed')}</ToggleButton>
                        <ToggleButton value="PERCENT">{t('rules.percent')}</ToggleButton>
                      </ToggleButtonGroup>
                      {row.mode === 'FIXED' ? (
                        <TextField
                          size="small"
                          label={t('rules.fixed')}
                          value={row.amount}
                          onChange={(event) =>
                            setTargets((prev) =>
                              prev.map((r) =>
                                r.key === row.key ? { ...r, amount: event.target.value } : r,
                              ),
                            )
                          }
                        />
                      ) : (
                        <TextField
                          size="small"
                          label={t('rules.percent')}
                          value={row.percent}
                          onChange={(event) =>
                            setTargets((prev) =>
                              prev.map((r) =>
                                r.key === row.key ? { ...r, percent: event.target.value } : r,
                              ),
                            )
                          }
                        />
                      )}
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel id={`rule-cat-${row.key}`}>
                          {t('rules.targetCategory')}
                        </InputLabel>
                        <Select
                          labelId={`rule-cat-${row.key}`}
                          label={t('rules.targetCategory')}
                          displayEmpty
                          value={row.categoryId === '' ? NONE : String(row.categoryId)}
                          onChange={(event) => {
                            const v = event.target.value;
                            setTargets((prev) =>
                              prev.map((r) =>
                                r.key === row.key
                                  ? { ...r, categoryId: v === NONE ? '' : Number(v) }
                                  : r,
                              ),
                            );
                          }}
                        >
                          <MenuItem value={NONE}>{t('rules.noCategory')}</MenuItem>
                          {cats.map((c) => (
                            <MenuItem key={c.id} value={String(c.id)}>
                              <CategoryChip id={c.id} name={c.name} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        size="small"
                        onClick={() =>
                          setTargets((prev) => {
                            if (index === 0) return prev;
                            const next = [...prev];
                            const cur = next[index];
                            const prevRow = next[index - 1];
                            if (cur === undefined || prevRow === undefined) return prev;
                            next[index - 1] = cur;
                            next[index] = prevRow;
                            return next;
                          })
                        }
                        disabled={index === 0}
                      >
                        {t('rules.moveUp')}
                      </Button>
                      <Button
                        size="small"
                        onClick={() =>
                          setTargets((prev) => {
                            if (index >= prev.length - 1) return prev;
                            const next = [...prev];
                            const cur = next[index];
                            const nextRow = next[index + 1];
                            if (cur === undefined || nextRow === undefined) return prev;
                            next[index + 1] = cur;
                            next[index] = nextRow;
                            return next;
                          })
                        }
                        disabled={index === targets.length - 1}
                      >
                        {t('rules.moveDown')}
                      </Button>
                      <Button
                        size="small"
                        onClick={() => setTargets((prev) => prev.filter((r) => r.key !== row.key))}
                        disabled={targets.length === 1}
                      >
                        {t('rules.delete')}
                      </Button>
                    </Stack>
                  );
                })}
                <Button
                  onClick={() => {
                    setTargets((prev) => [...prev, emptyTarget(`n${nextKey}`)]);
                    setNextKey((n) => n + 1);
                  }}
                >
                  {t('rules.addTarget')}
                </Button>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose}>{t('common.cancel')}</Button>
          <Button onClick={handleDryRun} data-confirm-opener={DRY_RUN_OPENER}>
            {t('rules.dryRun')}
          </Button>
          <SubmitButton
            onClick={() => void handleSave()}
            loading={submitting}
            data-confirm-opener={SAVE_OPENER}
          >
            {t('common.save')}
          </SubmitButton>
        </DialogActions>
      </GuardedDialog>
    </>
  );
}

function parseBound(input: string, currency: string | null, locale: string): number | null {
  if (input.trim().length === 0) return null;
  const n = parseMoneyToMinor(input, currency, locale);
  return n === null ? null : Math.abs(n);
}
